import { useRef } from 'react';

import { FileText, Paperclip, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { cx } from '@/lib/cx';

import styles from './AttachmentField.module.css';

/** What the server's own check accepts. Refusing these here saves a round trip. */
const ACCEPTED = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
];

const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Why this file cannot be sent, or null.
 *
 * Each rule mirrors one the server applies, so nothing leaves the browser that cannot
 * succeed. The empty-type case is the one worth naming: a file the browser could not
 * identify has no type, gets sent as a generic binary, and comes back as a 422 whose
 * message tells the person nothing about what was actually wrong.
 */
export function documentProblem(file: File): string | null {
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_BYTES) return 'That file is over 10 MB.';
  if (!ACCEPTED.includes(file.type)) {
    return 'Attach a PDF, a Word document, or an image.';
  }
  return null;
}

function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type AttachmentState = {
  file: File | null;
  /** 0 to 1 while uploading, null when not. */
  progress: number | null;
  error: string | null;
  done: boolean;
};

export const EMPTY_ATTACHMENT: AttachmentState = {
  file: null,
  progress: null,
  error: null,
  done: false,
};

/**
 * One document slot.
 *
 * The file stays in component state until the record exists, because a file cannot be
 * attached to a candidate that has not been created yet. That is also what makes a
 * failed upload survivable: the chosen file is still here, and retrying does not ask
 * anybody to find the document on their disk a second time.
 */
export function AttachmentField({
  label,
  hint,
  state,
  onPick,
  onClear,
  disabled = false,
}: {
  label: string;
  hint: string;
  state: AttachmentState;
  onPick: (file: File) => void;
  onClear: () => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploading = state.progress !== null;

  return (
    <div className={styles.slot}>
      <div className={styles.head}>
        <span className={styles.label}>
          <FileText size={15} aria-hidden="true" />
          {label}
        </span>
        {state.file ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled || uploading}
            icon={<X size={14} />}
            onClick={onClear}
          >
            Remove
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            icon={<Paperclip size={14} />}
            onClick={() => inputRef.current?.click()}
          >
            Choose a file
          </Button>
        )}
      </div>

      {state.file ? (
        <div className={styles.chosen}>
          <span className={styles.name}>{state.file.name}</span>
          <span className={styles.size}>{readableSize(state.file.size)}</span>
        </div>
      ) : (
        <p className={styles.hint}>{hint}</p>
      )}

      {uploading ? (
        <div className={styles.progress}>
          <div
            className={styles.track}
            role="progressbar"
            aria-label={`Uploading ${label}`}
            aria-valuenow={Math.round((state.progress ?? 0) * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={styles.bar}
              style={{ inlineSize: `${Math.round((state.progress ?? 0) * 100)}%` }}
            />
          </div>
          {/* At 100% the bytes are sent and the server is still working, which is
              what this says rather than sitting on "100%" looking stuck. */}
          <span className={styles.percent}>
            {(state.progress ?? 0) >= 1
              ? 'Finishing'
              : `${Math.round((state.progress ?? 0) * 100)}%`}
          </span>
        </div>
      ) : null}

      {state.done ? <p className={styles.done}>Attached.</p> : null}
      {state.error ? (
        <p className={cx(styles.hint, styles.error)} role="alert">
          {state.error}
        </p>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className={styles.input}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so choosing the same file twice still fires a change.
          event.target.value = '';
          if (file) onPick(file);
        }}
      />
    </div>
  );
}
